/* eslint-disable */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Material UI Imports
import {
    Box,
    InputLabel,
    MenuItem,
    FormControl,
    Select,
    Stack,
    TextField,
    Button,
    Tooltip,
    Grid,
    Typography,
    Chip,
    Autocomplete,
    useMediaQuery,
    useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

// Ant Design Imports
import { Spin, Space, Modal, Row, Col, Button as AntButton } from 'antd';
import { HomeOutlined } from '@ant-design/icons';

// Other Imports
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Project Utils & Components
import { gridSpacing } from 'store/constant';
import { handleCatchError } from 'utils/CatchHandler';
import GlobalBackButton from 'views/utilities/BackButtonGlobal';
import { globalNotifyError, globalNotifySuccess } from 'views/utilities/AlertToast';
import BcConvertModal from 'views/Components/BcConvertModal';
import Spinner from 'views/Components/LoadingSinner';
import { checkMerchantOrBCAPI, getPremiumBcChargesNewApi } from 'utils/API/Merchant APIs/MerchantApiCalls';
import { setDisablerFalse, setDisablerTrue } from 'redux/SidebarDisablerSlice';
import { fundTransferDmtApiCall, loadTransferApi } from 'utils/API/Merchant APIs/DmtApiCalls';
import TransferPageBioAuth from './DmtAuthComponents/TransferPageBioAuth';
import CommissionDisplayComponent from '../CommissionDisplayComponent';
import {
    fetchBeneficiaryList,
    fetchCustomerList,
    getCustomerLimit,
    getChargesDMTTransferApi
} from 'utils/API/Merchant APIs/UpdatedAPIs/DMTUpdatedAPICalls';

// Constants
const TRANSFER_TYPE = 'fino-transfer';
const DMT_TYPE_CHARGES = 2;
const DMT_TYPE_LIMIT = 1;
const MIN_TRANSFER_AMOUNT = 100;
const MAX_TRANSFER_AMOUNT = 5000;
const LIMIT_PER_PAGE = 10;

const commonStyles = { m: 0 };

export default function DenseTable() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state: locationState } = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Location State Extraction
    const CreateCustomerRes = locationState?.Data;
    const data = locationState?.item || locationState?.data;
    const initialCustomerId = data?.id || (data?.data?.[0]?.id) || locationState?.item?.id;
    const initialSenderNo = locationState?.senderno || (locationState?.filteredData?.find(c => c.id === initialCustomerId)?.mobileNumber) || '';
    const initialBeneficiaryId = locationState?.row?.id || '';
    const indexBenef = locationState?.indexBenef;

    // Redux State
    const balanceW1 = useSelector((state) => state?.wallet?.balance?.roundedNumber);

    // State Management
    const [paymentMode, setPaymentMode] = useState('IMPS');
    const [plansToDisplay, setPlansToDisplay] = useState({ normal: {}, silver: {}, gold: {} });
    const [selectedBenefData, setSelectedBenefData] = useState('');
    const [amountResponse, setAmountResponse] = useState(null);
    const [amountError, setAmountError] = useState('');
    const [customerMap, setCustomerMap] = useState([]);
    const [beneficiaryMap, setBeneficiaryMap] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(locationState?.Data || locationState?.item || (locationState?.data && !locationState?.item ? locationState.data : null));
    const [choosedBenef, setChoosedBenef] = useState(locationState?.row || null);
    const [tamount, setTamount] = useState('');
    const [senderno, setSenderno] = useState(initialSenderNo);
    const [maxLimit, setMaxLimit] = useState(0);

    // UI States
    const [isLoading, setIsLoading] = useState(false);
    const [maxLimitLoading, setMaxLimitLoading] = useState(false);
    const [isCalculatingCharges, setIsCalculatingCharges] = useState(false);
    const [isFailed, setIsFailed] = useState(false);
    const [isFailedMessage, setIsFailedMessage] = useState('');
    const [merchantModalOpen, setMerchantModalOpen] = useState(false);
    const [isBcConvertPending, setIsBcConvertPending] = useState(false);
    const [isCheckLoading, setIsCheckLoading] = useState(true);
    const [showOtpField, setShowOtpField] = useState(false);
    const [open, setOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // LocalStorage Values
    const shouldshowEsign = localStorage.getItem('shouldshowEsign');
    const premiumPlanStatus = localStorage.getItem('premium_Plan_Status') === 'true';
    const email = localStorage.getItem('email_id');
    const uid = localStorage.getItem('id');

    // Derived Values
    const id = selectedCustomer?.id || initialCustomerId;
    const benAcc = choosedBenef?.accountNumber || choosedBenef?.beneAccountNo || selectedBenefData?.beneAccountNo;
    const benIfsc = choosedBenef?.ifscCode || choosedBenef?.beneIfsc || selectedBenefData?.beneIfsc;
    const beneMobileNo = choosedBenef?.mobileNumber || choosedBenef?.beneMobile || selectedBenefData?.beneMobile;
    const amount = amountResponse?.amount || tamount;
    const beneName = choosedBenef?.beneficiaryFirstName
        ? `${choosedBenef.beneficiaryFirstName} ${choosedBenef.beneficiaryLastName || ''}`.trim()
        : (selectedBenefData?.beneName || choosedBenef?.name || choosedBenef?.beneficiaryFirstName);

    // API Calls
    const getPremiumPlansAndCharges = useCallback(async () => {
        try {
            const agentid = localStorage.getItem('id');
            const response = await getPremiumBcChargesNewApi({ agentid });
            const data = response?.data?.data || [];
            setPlansToDisplay({
                normal: data.find((item) => item.id === 1) || {},
                silver: data.find((item) => item.id === 3) || {},
                gold: data.find((item) => item.id === 2) || {}
            });
        } catch (error) {
            console.error('Failed to fetch premium plans:', error);
        }
    }, []);

    const fetchDetails = useCallback(async (pageNumber = 1) => {
        if (!initialCustomerId) return;
        try {
            if (pageNumber === 1) setLoadingMore(false);
            const response = await fetchBeneficiaryList(2, initialCustomerId, pageNumber, LIMIT_PER_PAGE);
            const { response: customerApiResponse } = await fetchCustomerList('fino');

            const customerList = customerApiResponse?.data?.data || [];
            const benefData = response?.data?.data?.result || [];
            const hasNext = response?.data?.data?.pagination?.hasNextPage;

            setCustomerMap(customerList);
            setBeneficiaryMap((prev) => (pageNumber === 1 ? benefData : [...prev, ...benefData]));
            setHasNextPage(hasNext);
            setLoadingMore(false);

            // Set selected customer from map if not already set
            if (!selectedCustomer && customerList.length > 0) {
                const current = customerList.find(c => c.id == initialCustomerId);
                if (current) setSelectedCustomer(current);
            }
        } catch (error) {
            console.error(error.message);
            handleCatchError(error);
            setLoadingMore(false);
        }
    }, [initialCustomerId, selectedCustomer]);

    const fetchCharges = async () => {
        if (!tamount || tamount < MIN_TRANSFER_AMOUNT) return;
        setIsCalculatingCharges(true);
        try {
            const payload = { dmtType: DMT_TYPE_CHARGES, paymentMode, amount: Number(tamount) };
            const values = await getChargesDMTTransferApi(payload);
            setAmountResponse(values?.data?.data);
            setOpen(true);
        } catch (error) {
            console.error(error.message);
            handleCatchError(error);
        } finally {
            setIsCalculatingCharges(false);
        }
    };

    const getTransactionLimit = useCallback(async () => {
        if (!senderno) return;
        try {
            setMaxLimitLoading(true);
            const payload = { customerMobileNo: senderno, dmtType: DMT_TYPE_LIMIT };
            const response = await getCustomerLimit(payload);
            const maximumLimit = response?.data?.data?.limit;
            setMaxLimit(maximumLimit >= 0 ? maximumLimit : 0);
        } catch (error) {
            console.error(error.message);
            handleCatchError(error);
        } finally {
            setMaxLimitLoading(false);
        }
    }, [senderno]);

    // Event Handlers
    const handleTransferClick = () => {
        setIsFailedMessage('');
        setIsFailed(false);
        if (Number(tamount) >= MIN_TRANSFER_AMOUNT) {
            if (!choosedBenef) {
                globalNotifyError('Please select a beneficiary');
                return;
            }
            fetchCharges();
        } else {
            setAmountError(`The minimum transfer amount is ${MIN_TRANSFER_AMOUNT}`);
            globalNotifyError(`The minimum transfer amount is ${MIN_TRANSFER_AMOUNT}`);
        }
    };

    const handleOk = () => {
        setOpen(false);
        dispatch(setDisablerFalse());
        setShowOtpField(true);
    };

    const handleCancel = () => {
        setOpen(false);
        dispatch(setDisablerFalse());
    };

    const handleAmountChange = (e) => {
        setAmountError('');
        const amountValue = e.target.value.replace(/[^0-9.]/g, '');
        if (/^\d*\.?\d*$/.test(amountValue)) {
            if (parseFloat(amountValue) > MAX_TRANSFER_AMOUNT) {
                setAmountError(`Amount cannot exceed ${MAX_TRANSFER_AMOUNT}`);
            } else {
                setTamount(amountValue);
            }
        } else {
            setAmountError('Enter valid numbers only');
        }
    };

    const validateAmount = () => {
        const amountNum = parseFloat(tamount);
        const balanceNum = parseFloat(balanceW1);
        if (amountNum > balanceNum) {
            setAmountError('Entered amount exceeds the wallet balance!');
        } else if (amountNum > maxLimit) {
            setAmountError('The entered amount exceeds the maximum limit');
        } else {
            setAmountError('');
        }
    };

    // Effects
    useEffect(() => {
        getPremiumPlansAndCharges();
        const checkStatus = async () => {
            setIsCheckLoading(true);
            try {
                const agentid = localStorage.getItem('id');
                const res = await checkMerchantOrBCAPI({ agentid });
                const { agentType, isPending } = res?.data || {};
                setIsBcConvertPending(isPending);
                if (agentType === 'Merchant' || isPending || shouldshowEsign === 'true') {
                    setMerchantModalOpen(true);
                }
            } catch (error) {
                console.error('Merchant check failed', error);
            } finally {
                setIsCheckLoading(false);
            }
        };
        checkStatus();
        if (CreateCustomerRes) globalNotifySuccess('Customer created successfully');
    }, [getPremiumPlansAndCharges, CreateCustomerRes, shouldshowEsign]);

    useEffect(() => {
        if (senderno && initialCustomerId) {
            getTransactionLimit();
            setPage(1);
            fetchDetails(1);
        }
    }, [initialCustomerId, senderno, getTransactionLimit, fetchDetails]);

    useEffect(() => {
        if (open) dispatch(setDisablerTrue());
    }, [open, dispatch]);

    useEffect(() => {
        if (beneficiaryMap.length > 0 && indexBenef) {
            const matched = beneficiaryMap.find((item) => item.id === indexBenef);
            if (matched) setChoosedBenef(matched);
        }
    }, [beneficiaryMap, indexBenef]);

    useEffect(() => {
        if (isFailed) globalNotifyError(isFailedMessage);
    }, [isFailed, isFailedMessage]);

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    backgroundColor: '#fff',
                    boxShadow: 2,
                    borderRadius: 2,
                    p: isMobile ? '20px 10px' : 2,
                    mb: 2
                }}
            >
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <Typography variant="h3">
                            <GlobalBackButton /> Domestic Money Transfer
                        </Typography>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 1 }}>
                    <Link to="/merchant-home" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <HomeOutlined />
                    </Link>
                    <Typography component="span" sx={{ mx: 1 }}>&gt;</Typography>
                    <Link to="/moneyTransfer1/viewCustomers" style={{ textDecoration: 'none', color: 'inherit' }}>
                        View Customers
                    </Link>
                    <Typography component="span" sx={{ mx: 1 }}>&gt;</Typography>
                    <Typography component="span">Money Transfer</Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={gridSpacing}>
                        <Grid item lg={4} md={6} xs={12}>
                            <Chip
                                color="info"
                                label={`Selected Customer: ${selectedCustomer?.customerName || ''}`}
                                sx={{ fontWeight: 'bold' }}
                            />
                        </Grid>
                        <Grid item lg={4} md={6} xs={12}>
                            <Chip
                                color="warning"
                                label={maxLimitLoading ? "Loading..." : `Transaction Limit : ₹ ${maxLimit}`}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            <Box sx={{ width: '100%', backgroundColor: '#fff', p: isMobile ? '20px 10px' : 2 }}>
                <Grid container spacing={2}>
                    {/* Summary Info Row */}
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            {[
                                { label: 'SENDER MOBILE', value: senderno },
                                { label: 'BENEFICIARY A/C', value: benAcc },
                                { label: 'BENEFICIARY IFSC', value: benIfsc },
                                { label: 'BENEFICIARY Mobile', value: beneMobileNo }
                            ].map((item, idx) => (
                                <Grid item lg={3} md={6} xs={6} key={idx}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        {item.label} : {item.value || '-'}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Inputs Row */}
                    <Grid item lg={4} md={6} xs={12} sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>IMPS/NEFT</InputLabel>
                            <Select
                                value={paymentMode}
                                label="IMPS/NEFT"
                                onChange={(e) => setPaymentMode(e.target.value)}
                            >
                                <MenuItem value="IMPS">IMPS</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item lg={4} md={6} xs={12} sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1}>
                            <Autocomplete
                                fullWidth
                                options={beneficiaryMap}
                                value={choosedBenef}
                                onChange={(_, val) => setChoosedBenef(val)}
                                getOptionLabel={(opt) => `${opt?.beneficiaryFirstName || ''} ${opt?.beneficiaryLastName || ''}`}
                                ListboxProps={{
                                    sx: { maxHeight: '250px', overflow: 'auto' },
                                    onScroll: (e) => {
                                        const list = e.currentTarget;
                                        if (!hasNextPage || loadingMore) return;
                                        if (Math.ceil(list.scrollTop + list.clientHeight) >= list.scrollHeight - 20) {
                                            setLoadingMore(true);
                                            setPage(p => p + 1);
                                            fetchDetails(page + 1);
                                        }
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} label="Beneficiary" />}
                            />
                            <Tooltip title="Click to add new beneficiary">
                                <Link to="/moneyTransfer1/createBeneficiary" state={{ data, customerId: initialCustomerId }}>
                                    <AntButton shape="circle" type="primary" icon={<AddIcon />} style={{ marginTop: '8px' }} />
                                </Link>
                            </Tooltip>
                        </Stack>
                    </Grid>

                    <Grid item lg={4} md={6} xs={12} sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <TextField
                                fullWidth
                                label="Amount"
                                value={tamount}
                                error={Boolean(amountError)}
                                helperText={amountError}
                                onChange={handleAmountChange}
                                onBlur={validateAmount}
                            />
                            {isCalculatingCharges && <Spin size="small" />}
                        </Stack>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 3 }}>
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={Boolean(amountError) || isLoading || isCalculatingCharges || !choosedBenef || !tamount}
                            onClick={handleTransferClick}
                            fullWidth
                            size="large"
                        >
                            {isLoading ? 'Processing...' : 'Transfer'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Modal
                title={<Chip color="info" label="CONFIRM MONEY TRANSFER DETAILS" />}
                open={open}
                onOk={handleOk}
                onCancel={handleCancel}
                width={isMobile ? "95%" : "85%"}
            >
                <Row gutter={[16, 16]} sx={{ mt: 2 }}>
                    <Col span={isMobile ? 24 : 12}>
                        <p><strong>Customer Name:</strong> {selectedCustomer?.customerName || ''}</p>
                        <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                        <p><strong>Beneficiary A/C:</strong> {benAcc}</p>
                        <p><strong>Service Charge:</strong> ₹{amountResponse?.newserviceCharge ?? '0'}</p>
                        <p><strong>GST:</strong> ₹{amountResponse?.gst ?? '0'}</p>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <p><strong>Beneficiary Name:</strong> {beneName}</p>
                        <p><strong>Payment Mode:</strong> {paymentMode}</p>
                        <p><strong>Beneficiary IFSC:</strong> {benIfsc}</p>
                        <p><strong>Effective Commission:</strong> ₹{amountResponse?.agent_commission ?? '0'}</p>
                        <p><strong>TDS:</strong> ₹{amountResponse?.tds ?? '0'}</p>
                        {plansToDisplay?.gold?.upgradeStatus === 1 && (
                            <p><strong>Total Commission:</strong> ₹{amountResponse?.totCommi ?? '0'}</p>
                        )}
                    </Col>
                </Row>
                {premiumPlanStatus && (plansToDisplay?.normal?.upgradeStatus === 1 || plansToDisplay?.silver?.upgradeStatus === 1) && (
                    <CommissionDisplayComponent
                        amountResponse={amountResponse}
                        isSilver={plansToDisplay?.silver?.upgradeStatus === 1}
                    />
                )}
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 'bold' }}>
                    Total Debit Amount: ₹ {amountResponse?.agentDebitAmount ?? '0'}
                </Typography>
            </Modal>

            <ToastContainer />
            {merchantModalOpen && (
                <BcConvertModal
                    isOpen={merchantModalOpen}
                    handleclose={() => setMerchantModalOpen(false)}
                    isPending={isBcConvertPending}
                    shouldshowEsign={shouldshowEsign}
                />
            )}
            {showOtpField && (
                <TransferPageBioAuth
                    showModal={showOtpField}
                    id={id}
                    benAcc={benAcc}
                    beneName={beneName}
                    beneId={choosedBenef?.id}
                    amount={amount}
                    paymentMode={paymentMode}
                    data={data}
                    closeModalView={() => setShowOtpField(false)}
                />
            )}
            <Spinner loading={isCheckLoading} />
        </>
    );
}
